import { useCallback, useState } from 'react';
import { Modal as RNModal } from 'react-native';
import { useNavigate, useParams } from 'react-router';
import { showMessage } from 'react-native-flash-message';

import { trackEvent } from '~/gtag';
import { LayoutWithSidebar } from '~/layouts/LayoutWithSidebar';
import { MultiClassEditScreen } from '~/screens/multiclassEdit';
import { ArchivedClassesListScreen } from '~/screens/archivedClassesList';
import { Period } from '~/services/types';
import { usePeriodListAllQuery } from '~/services/period';
import { useViewArchivedClassesFeature } from '~/featureFlags';
import { Loading } from '~/components/Loading';

type MainLayoutProps = {
  children: React.ReactNode;
  onAfterPeriodSelect?: () => void;
};

export function MainLayout({ children, onAfterPeriodSelect }: MainLayoutProps) {
  const { periodShortName } = useParams();
  const navigate = useNavigate();
  const { data, status } = usePeriodListAllQuery();
  const { periods } = data || {};
  const isArchivedClassesEnabled = useViewArchivedClassesFeature();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  const activePeriod = periods?.find((p: Period) => p.shortName === periodShortName) || periods?.[0];

  const onSelectPeriod = useCallback(
    (periodId: string) => {
      const newPeriod = periods?.find((p: Period) => p.id === periodId);
      if (newPeriod) {
        trackEvent('period_switch');
        navigate(`/review/${newPeriod.shortName}`);
        onAfterPeriodSelect?.();
      }
    },
    [periods, navigate, onAfterPeriodSelect],
  );

  const handleCloseArchivedClasses = (flashMessage?: string, type?: string, shortName?: string) => {
    setArchiveModalOpen(false);

    if (!!flashMessage && !!type) {
      showMessage({ message: flashMessage, type: type as 'success' | 'warning' });
    }

    if (!!shortName) {
      navigate(`/review/${shortName}`);
    }
  };

  if (status === 'pending') {
    return <Loading />;
  }

  return (
    <>
      <LayoutWithSidebar
        isSimple={false}
        periods={periods || []}
        activePeriodId={activePeriod?.id}
        onSelectPeriod={onSelectPeriod}
        onEditPress={() => {
          trackEvent('edit_schedule');
          setEditModalOpen(true);
        }}
        onArchivedClassesPress={() => {
          trackEvent('view_archived_classes');
          setArchiveModalOpen(true);
        }}>
        {children}
      </LayoutWithSidebar>
      {periods && (
        <>
          <RNModal visible={editModalOpen} onRequestClose={() => setEditModalOpen(false)} animationType="none">
            <MultiClassEditScreen periods={periods} onClose={() => setEditModalOpen(false)} />
          </RNModal>
          {isArchivedClassesEnabled && (
            <RNModal visible={archiveModalOpen} onRequestClose={() => setArchiveModalOpen(false)} animationType="none">
              <ArchivedClassesListScreen onClose={handleCloseArchivedClasses} />
            </RNModal>
          )}
        </>
      )}
    </>
  );
}
